import { type ICellRendererParams } from "ag-grid-community"
import { type CustomCellRendererProps } from "ag-grid-react"
import { ArrowSquareOut } from "@phosphor-icons/react"
import { first } from "lodash"
import { type FunctionComponent, type MouseEvent, type ReactNode } from "react"

import { IconButton } from "~/components/reusable/Button"
import ProductImage from "~/components/reusable/ProductImage"
import { ItemIdentity } from "~/components/reusable/ItemIdentity"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { getFilteredLeafData } from "~/utils/ag-utils"
import { formatSkuId, formatVariantId } from "~/utils/helpers"
import { selectBestImageUrlFromObjects } from "~/utils/imageUtils"
import { rowLabel } from "./utils/rowLabels"

export const CellProductImage = ({ imageUrl, tile }: { imageUrl?: string; tile?: boolean }) => {
  return (
    <div className="relative min-w-[28px] ml-0.5">
      <ProductImage url={imageUrl} alt="Product image" width={28} tile={tile} />
    </div>
  )
}

export const OpenCellButton = ({ onClick }: { onClick: (e: MouseEvent) => void }) => (
  <div className="open-cell-button flex items-center">
    <IconButton
      variant="neutral"
      size="sm"
      title="Open"
      onClick={(e) => {
        e.stopPropagation()
        onClick(e)
      }}
    >
      <ArrowSquareOut size={16} />
    </IconButton>
  </div>
)

type CellRendererProps = CustomCellRendererProps<IVariantSummary> & {
  aggregations: string[]
  onOpen?: (e: MouseEvent) => void
  /** Row action rendered in place of the open button — e.g. an inspector peek toggle. */
  action?: ReactNode
  tile?: boolean
}

export const VariantCellRenderer: FunctionComponent<CellRendererProps> = (props) => {
  const { node, aggregations, onOpen, action, tile } = props
  // A pinned totals footer carries no product data, and the "Unknown product" fallback below
  // would render it as a broken variant. A pinned row that does carry one (the match dialog pins
  // the variant being matched to the top) renders as itself.
  if (node?.rowPinned && !node.data?.variantId) return <div className="label-sm px-1">Total</div>
  const entry = node?.data
  const variantId = entry?.variantId ?? "Unknown variant"
  const imageUrl = entry?.imageUrl ?? ""

  const title = rowLabel(node, aggregations)

  return (
    <div className="relative flex flex-row items-center gap-4 h-[40px] w-[100%]">
      <ItemIdentity
        title={title}
        subtitle={`#${formatVariantId(variantId)}`}
        imageUrl={imageUrl}
        gapClassName="gap-4"
        className="flex-1"
        tile={tile}
      />
      {action ?? (onOpen && <OpenCellButton onClick={onOpen} />)}
    </div>
  )
}

export const ProductCellRenderer: FunctionComponent<CellRendererProps> = (props) => {
  const { node, aggregations, onOpen, action, tile } = props
  // Pinned rows (e.g. a totals footer) have no leaf children/real product data — see the
  // matching rowPinned check in VariantCellRenderer above.
  if (node?.rowPinned) return <div className="label-sm px-1">Total</div>
  const entries = getFilteredLeafData(node)
  const productId = first(entries)?.productId ?? "Unknown product"
  const variantId = formatVariantId(first(entries)?.variantId ?? "Unknown variant")
  const imageUrl = selectBestImageUrlFromObjects(entries) ?? ""

  const title = rowLabel(node, aggregations)

  return (
    <div className="relative flex flex-row items-center gap-4 h-[40px] w-[100%]">
      <ItemIdentity
        title={title}
        subtitle={`#${node.field === "variantId" ? formatVariantId(variantId) : productId}`}
        imageUrl={imageUrl}
        gapClassName="gap-4"
        className="flex-1"
        tile={tile}
      />
      {action ?? (onOpen && <OpenCellButton onClick={onOpen} />)}
    </div>
  )
}

export const SkuCellRenderer: React.FC<ICellRendererParams> = (params) => {
  return (
    <div className="flex flex-col items-start justify-center h-[40px] w-[100%] leading-5">
      <div className="label-sm">{params.data?.size}</div>
      <div className="text-fg-muted paragraph-sm">#{formatSkuId(params.data?.skuId)}</div>
    </div>
  )
}
